import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, X, Save, Package, AlertTriangle, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import apiClient from '../api/client';

const InventoryManager = () => {
  const { showToast } = useToast();
  const [inventory, setInventory] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [formData, setFormData] = useState<{ name: string; unit: string; stock: number; minStock: number; category: string; revision?: number }>({
    name: '',
    unit: 'unidades',
    stock: 0,
    minStock: 10,
    category: 'otros'
  });

  const fetchInventory = useCallback(async () => {
    try {
      const res = await apiClient.get('/inventory');
      setInventory(res.data);
    } catch (error) {
      showToast('Error al cargar inventario', 'error');
    }
  }, [showToast]);

  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ ...item });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        unit: 'unidades',
        stock: 0,
        minStock: 10,
        category: 'otros'
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await apiClient.put(`/inventory/${editingItem._id}`, { ...formData, expectedRevision: formData.revision });
        showToast('Insumo actualizado', 'success');
      } else {
        await apiClient.post('/inventory', formData);
        showToast('Insumo creado', 'success');
      }
      setIsModalOpen(false);
      fetchInventory();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error al guardar insumo', 'error');
    }
  };

  const handleDelete = async (item: any) => {
    if (!window.confirm(`¿Eliminar el insumo "${item.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await apiClient.delete(`/inventory/${item._id}`);
      showToast('Insumo eliminado', 'success');
      fetchInventory();
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Error al eliminar insumo', 'error');
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Package className="text-primary" />
          Gestión de Inventario
        </h2>
        <Button onClick={() => handleOpenModal()} className="rounded-xl flex items-center gap-2">
          <Plus size={20} />
          Nuevo Insumo
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {inventory.map((item: any) => (
          <Card key={item._id} className={`p-6 group relative overflow-hidden border-2 transition-all ${item.stock < item.minStock ? 'border-red-500/20 bg-red-50/30 dark:bg-red-900/10' : 'border-transparent'}`}>
            <div className="flex justify-between items-start mb-4">
              <div className={`p-2 rounded-lg ${item.stock < item.minStock ? 'bg-red-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                <Package size={20} />
              </div>
              <div className="flex gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon" onClick={() => handleOpenModal(item)} className="h-8 w-8 rounded-lg bg-white dark:bg-slate-800 shadow-sm">
                  <Edit2 size={14} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(item)} aria-label={`Eliminar ${item.name}`} className="h-8 w-8 rounded-lg bg-white dark:bg-slate-800 shadow-sm text-red-500">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
            
            <h3 className="text-lg font-black truncate">{item.name}</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">{item.category}</p>
            
            <div className="flex items-end justify-between">
              <div>
                <p className="text-3xl font-black text-slate-900 dark:text-white">
                  {item.stock}
                </p>
                <p className="text-xs font-bold text-slate-500 uppercase">{item.unit}</p>
              </div>
              {item.stock < item.minStock && (
                <div className="flex items-center gap-1 text-red-500 animate-pulse">
                  <AlertTriangle size={16} />
                  <span className="text-[10px] font-black uppercase">Stock Bajo</span>
                </div>
              )}
            </div>

            {/* Progress Bar */}
            <div className="mt-4 h-1.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
              <div 
                className={`h-full transition-all duration-500 ${item.stock < item.minStock ? 'bg-red-500' : 'bg-green-500'}`}
                style={{ width: `${Math.min((item.stock / (item.minStock * 2)) * 100, 100)}%` }}
              />
            </div>
          </Card>
        ))}
      </div>

      {/* Modal Form */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative bg-white dark:bg-slate-950 w-full max-w-md rounded-[32px] shadow-2xl overflow-hidden"
            >
              <form onSubmit={handleSubmit} className="p-8">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-3xl font-black">
                    {editingItem ? 'Editar Insumo' : 'Nuevo Insumo'}
                  </h2>
                  <Button type="button" variant="ghost" size="icon" onClick={() => setIsModalOpen(false)}>
                    <X size={24} />
                  </Button>
                </div>

                <div className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="inventory-name" className="text-sm font-bold text-slate-500 uppercase">Nombre del Insumo (obligatorio)</label>
                    <input 
                      id="inventory-name"
                      required
                      placeholder="Ej: Pan Francés, Chorizo..."
                      className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="inventory-stock" className="text-sm font-bold text-slate-500 uppercase">Stock Actual (obligatorio)</label>
                      <input 
                        id="inventory-stock"
                        required
                        type="number"
                        className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                        value={formData.stock}
                        onChange={e => setFormData({...formData, stock: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="inventory-min-stock" className="text-sm font-bold text-slate-500 uppercase">Stock Mínimo (obligatorio)</label>
                      <input 
                        id="inventory-min-stock"
                        required
                        type="number"
                        className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                        value={formData.minStock}
                        onChange={e => setFormData({...formData, minStock: parseFloat(e.target.value)})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label htmlFor="inventory-unit" className="text-sm font-bold text-slate-500 uppercase">Unidad (obligatoria)</label>
                      <select 
                        id="inventory-unit"
                        required
                        className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                        value={formData.unit}
                        onChange={e => setFormData({...formData, unit: e.target.value})}
                      >
                        <option value="unidades">Unidades</option>
                        <option value="gramos">Gramos (g)</option>
                        <option value="kilogramos">Kilogramos (kg)</option>
                        <option value="ml">Mililitros (ml)</option>
                        <option value="litros">Litros (L)</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="inventory-category" className="text-sm font-bold text-slate-500 uppercase">Categoría (obligatoria)</label>
                      <select 
                        id="inventory-category"
                        required
                        className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                        value={formData.category}
                        onChange={e => setFormData({...formData, category: e.target.value})}
                      >
                        <option value="panes">Panes</option>
                        <option value="embutidos">Embutidos</option>
                        <option value="bebidas">Bebidas</option>
                        <option value="salsas">Salsas</option>
                        <option value="otros">Otros</option>
                      </select>
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-16 rounded-2xl text-lg font-black mt-4 flex items-center justify-center gap-2 shadow-lg shadow-primary/20">
                    <Save size={20} />
                    {editingItem ? 'Actualizar Stock' : 'Crear Insumo'}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InventoryManager;
